import type { Diagnostic, DiagnosticKind, DiagnosticSignal } from "./types";
import { money } from "./format";

export interface DiagnosticMeta {
  label: string;
  /** Plain-language meaning (client-readable). */
  meaning: string;
  /** One-line "what to check next". */
  action: string;
}

/**
 * Display copy per pill. Diagnostic pills render in amber (warning) to set them
 * apart from the red hard-flags (Drop / Creative Fatigue / High Frequency).
 */
export const DIAGNOSTIC_META: Record<DiagnosticKind, DiagnosticMeta> = {
  upstream: {
    label: "Upstream issue",
    meaning:
      "The ad is losing its pull — fewer people click, so you pay more per result. The problem is at the ad level.",
    action: "creative, copy, and targeting.",
  },
  downstream: {
    label: "Downstream issue",
    meaning:
      "The ad still earns clicks, but they don't convert — so the ad isn't the problem, the post-click experience is.",
    action: "landing page, offer, price, stock, and checkout.",
  },
  wrong_clicks: {
    label: "Wrong clicks",
    meaning:
      "Lots of clicks (relative to your other campaigns) but few conversions — the ad may promise something the landing page doesn't deliver, or the audience is off.",
    action: "creative-to-landing match and audience targeting.",
  },
  fatigue_incoming: {
    label: "Fatigue Incoming",
    meaning:
      "CTR is sliding on an active creative — an early fatigue warning, before cost fully blows out. Separate from the age-based Creative Fatigue flag.",
    action: "refresh the creative soon; watch cost per conversion.",
  },
};

export const DIAGNOSTIC_ORDER: DiagnosticKind[] = [
  "upstream",
  "downstream",
  "wrong_clicks",
  "fatigue_incoming",
];

function pctStr(v: number): string {
  const p = v * 100;
  return `${Math.abs(p) >= 1 ? p.toFixed(1) : p.toFixed(2)}%`;
}

/** Format one signal for the tooltip, e.g. "CTR 2.1% → 1.3% (-38%)". */
export function formatSignal(s: DiagnosticSignal, currency: string): string {
  const fmt = (v: number | null): string => {
    if (v == null) return "—";
    if (s.unit === "currency") return money(v, currency, 2);
    if (s.unit === "pct") return pctStr(v);
    return Math.round(v).toLocaleString("en-US"); // count
  };
  const delta =
    s.deltaPct == null ? "" : ` (${s.deltaPct > 0 ? "+" : ""}${Math.round(s.deltaPct * 100)}%)`;
  return `${s.label} ${fmt(s.from)} → ${fmt(s.to)}${delta}`;
}

/** The full hover tooltip: meaning + the exact triggering signals + what to check. */
export function buildDiagnosticTooltip(d: Diagnostic, currency: string): string {
  const meta = DIAGNOSTIC_META[d.kind];
  const lines = d.signals.map((s) => formatSignal(s, currency));
  return `${meta.label} — ${meta.meaning}\n${lines.join("\n")}\nCheck: ${meta.action}`;
}
